<template>
  <div class="sidebar" :class="{ 'sidebar-themed': sidebarTheme.configured, 'sidebar-theme-enabled': serviceThemeEnabled }" :style="sidebarTheme.variables">
    <div class="sidebar-header" >
      <h3>服务平台</h3>
    </div>
    <el-menu
      :default-active="activeMenu"
      text-color="#333"
      :active-text-color="sidebarTheme.activeText"
      ref="menu"
      @select="handleMenuSelect"
    >
      <template v-for="item in menuItems">
        <!-- 有子菜单的情况 -->
        <el-submenu
          v-if="item.childrenList && item.childrenList.length"
          :key="item.menuCode"
          :index="item.menuCode"
        >
          <template slot="title">
            <span class="sidebar-menu-icon" aria-hidden="true">
              <span v-if="getMenuIcon(item)" class="sidebar-menu-image" :style="getMenuIconStyle(item)">
                <img :src="getMenuIcon(item)" alt="" @error="handleMenuIconError(item)">
              </span>
              <i v-else class="el-icon-folder-opened"></i>
            </span>
            <span>{{ item.menuName }}</span>
          </template>
          <el-menu-item
            v-for="child in item.childrenList"
            :key="child.menuCode"
            :index="child.menuUrl"
          >
            <span slot="title">{{ child.menuName }}</span>
          </el-menu-item>
        </el-submenu>

        <!-- 无子菜单的情况 -->
        <el-menu-item
          v-else
          :key="item.menuCode"
          :index="item.menuUrl"
        >
          <span class="sidebar-menu-icon" aria-hidden="true">
            <span v-if="getMenuIcon(item)" class="sidebar-menu-image" :style="getMenuIconStyle(item)">
              <img :src="getMenuIcon(item)" alt="" @error="handleMenuIconError(item)">
            </span>
            <i v-else :class="item.menuCode === 'invoiceManagement' ? 'el-icon-tickets' : 'el-icon-menu'"></i>
          </span>
          <span slot="title">{{ item.menuName }}</span>
        </el-menu-item>
      </template>
    </el-menu>
    <div v-if="menuError" class="menu-feedback" role="alert">
      <span>{{ menuError }}</span>
      <el-button type="text" size="small" :loading="menuLoading" @click="getMenuItems">重新加载</el-button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { activeServiceMenu, flattenServiceMenus, activateServiceMenu } from '@/utils/serviceMenus'
import { serviceSidebarTheme } from '@/utils/serviceSidebarTheme'

export default {
  data() {
    return { failedMenuIcons: {} }
  },
  computed: {
    ...mapState('serviceNavigation', { menuItems: 'menus', menuError: 'menuError', menuLoading: 'menuLoading', selectionRevision: 'selectionRevision' }),
    ...mapGetters('template', ['serviceThemeEnabled']),
    sidebarTheme() {
      if (this.serviceThemeEnabled) return serviceSidebarTheme(this.$store.state.template.globalStyle)
      return { configured: false, activeText: '#165DFF', variables: null }
    },
    activeMenu() {
      const menu = activeServiceMenu(this.menuItems, this.$route)
      return menu ? menu.menuUrl : this.$route.path
    },
    activeParent() {
      const menu = activeServiceMenu(this.menuItems, this.$route)
      return menu && menu.parentCode || ''
    }
  },
  watch: {
    activeParent: { immediate: true, handler() { this.expandActiveParent() } },
    selectionRevision() { this.expandActiveParent() }
  },
  methods: {
    getMenuItems() { this.$store.dispatch('serviceNavigation/loadMenus', true) },
    expandActiveParent() {
      this.$nextTick(() => {
        if (this.activeParent && this.$refs.menu) this.$refs.menu.open(this.activeParent)
      })
    },
    isMenuActive(item) {
      return item.menuUrl === this.activeMenu || (item.childrenList || []).some(child => child.menuUrl === this.activeMenu)
    },
    getMenuIcon(item) {
      if (item.menuCode === 'invoiceManagement') return null
      const prefixes = this.isMenuActive(item) ? ['activity', ''] : ['']
      for (const prefix of prefixes) {
        try {
          const asset = require(`@/static/images/service-icon/slideBar/${prefix}${item.menuCode}.png`)
          const src = typeof asset === 'string' ? asset : asset.default
          if (src && !this.failedMenuIcons[src]) return src
        } catch (e) { /* 未配置图片的菜单使用字体图标。 */ }
      }
      return null
    },
    handleMenuIconError(item) {
      const src = this.getMenuIcon(item)
      if (src) this.$set(this.failedMenuIcons, src, true)
    },
    getMenuIconStyle(item) {
      const image = `url("${this.getMenuIcon(item)}")`
      return { maskImage: image, WebkitMaskImage: image, '-webkit-mask-image': image }
    },
    handleMenuSelect(index) {
      const menu = flattenServiceMenus(this.menuItems).find(item => item.menuUrl === index)
      if (menu && menu.menuCode === 'exit') {
        this.$store.dispatch('userOutLogin').then(() => this.$router.push('/public/service/login'))
      } else if (activateServiceMenu(this.$router, menu)) {
        this.$store.commit('serviceNavigation/TOUCH_MENU')
      }
    }
  }
}
</script>

<style lang="scss" scoped>

.el-menu {
  border-right: none;
}
.sidebar-menu-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 13px;
  height: 13px;
  margin-right: 10px;
  vertical-align: middle;
  color: #666;
  i {
    width: 13px;
    margin: 0;
    font-size: 13px;
    line-height: 13px;
    color: inherit;
  }
}
.el-menu-item.is-active .sidebar-menu-icon,
.el-submenu.is-active .sidebar-menu-icon {
  color: var(--service-sidebar-active-text, #165DFF);
}
.sidebar-menu-image {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;
  width: 13px;
  height: 13px;
  mask-size: contain;
  mask-position: center;
  mask-repeat: no-repeat;
  -webkit-mask-size: contain;
  -webkit-mask-position: center;
  -webkit-mask-repeat: no-repeat;
  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}
@supports ((mask-image: url("")) or (-webkit-mask-image: url(""))) {
  .sidebar-theme-enabled .sidebar-menu-image { background-color: currentColor; }
  .sidebar-theme-enabled .sidebar-menu-image img { visibility: hidden; }
}
.menu-feedback { padding: 8px 16px; font-size: 12px; color: #606266; }
.menu-feedback .el-button { margin-left: 8px; }
.sidebar {
  width: 200px;
  min-height: 80vh;
  box-sizing: border-box;
  position: relative;
  flex-shrink: 0;

  background-image: url('../static/images/service-icon/leftMenu.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  &.sidebar-themed {
    background-image: linear-gradient(180deg, var(--service-sidebar-surface, #fff), var(--service-sidebar-tail, #fff));
  }

  border: 1px solid #E4E4E4; // 边框
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.08); // 阴影
  border-radius: 10px; // 与首页卡片保持一致
  transition: all 0.3s ease;
  .sidebar-header {
    height: 60px;
    line-height: 60px;
    padding-left: 20px;
    background: var(--service-sidebar-title-bg, #045ad5);
    color: var(--service-sidebar-title-text, #fff);
    border-bottom: 1px solid #E4E4E4;
    border-radius: 9px 9px 0 0; // 扣除外层 1px 边框，避免标题背景盖住圆角
    h3 {
      margin: 0;
      padding: 0;
      font-weight: 600;
    }
  }
  .el-menu {
    /* 悬停与选中均使用侧栏局部主题。 */
    background: transparent;
    .el-menu-item {
      &:hover {
        background-color: var(--service-sidebar-hover-bg, #f1f5ff) !important;
      }
      
      /* 激活状态样式 */
      &.is-active {
        background-color: var(--service-sidebar-active-bg, rgba(22, 93, 255, 0.1)) !important;
        font-weight: 600;
      }
    }
  }
}
.sidebar ::v-deep .el-menu--inline { background: transparent; }
.sidebar ::v-deep .el-submenu__title:hover { background-color: var(--service-sidebar-hover-bg, #f1f5ff); }
.sidebar ::v-deep .el-submenu.is-active > .el-submenu__title {
  color: var(--service-sidebar-active-text, #165DFF) !important;
}
</style>
